import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface SearchResult {
  id: string;
  title: string;
  content: string;
  type: 'topic' | 'post' | 'category';
  author_username: string;
  category_name: string;
  category_color: string;
  category_slug?: string;
  slug?: string;
  topic_title?: string;
  topic_slug?: string;
  reply_count: number;
  view_count: number;
  created_at: string;
}

export type SearchFilter = 'all' | 'categories' | 'topics' | 'posts';

export const useSearch = (query: string, filter: SearchFilter = 'all') => {
  return useQuery({
    queryKey: ['search', query, filter],
    queryFn: async (): Promise<SearchResult[]> => {
      if (!query || query.trim().length < 2) {
        return [];
      }

      const searchTerm = `%${query.trim()}%`;
      const results: SearchResult[] = [];
      
      // Search in categories
      if (filter === 'all' || filter === 'categories') {
        const { data: categoryResults } = await supabase
          .from('categories')
          .select('id, name, description, color, slug')
          .or(`name.ilike.${searchTerm},description.ilike.${searchTerm}`)
          .limit(50);

        categoryResults?.forEach(cat => {
          results.push({
            id: cat.id,
            title: cat.name,
            content: cat.description || '',
            type: 'category' as const,
            author_username: 'System',
            category_name: cat.name,
            category_color: cat.color || '#228B22',
            category_slug: cat.slug,
            reply_count: 0,
            view_count: 0,
            created_at: new Date().toISOString()
          });
        });
      }

      // Search in topics
      if (filter === 'all' || filter === 'topics') {
        const { data: topicResults } = await supabase
          .from('topics')
          .select(`
            id,
            title,
            content,
            slug,
            created_at,
            reply_count,
            view_count,
            user_id,
            category_id,
            categories (name, color, slug)
          `)
          .or(`title.ilike.${searchTerm},content.ilike.${searchTerm}`)
          .order('created_at', { ascending: false })
          .limit(50);

        // Get user profiles
        const userIds = [...new Set(topicResults?.map(t => t.user_id).filter(Boolean))] as string[];
        let profilesMap = new Map();

        if (userIds.length > 0) {
          const { data: profiles } = await supabase
            .from('profiles')
            .select('id, display_name')
            .in('id', userIds);

          profiles?.forEach(profile => {
            profilesMap.set(profile.id, profile);
          });
        }

        topicResults?.forEach(topic => {
          const category = topic.categories as any;
          const profile = topic.user_id ? profilesMap.get(topic.user_id) : null;

          results.push({
            id: topic.id,
            title: topic.title,
            content: topic.content,
            type: 'topic' as const,
            author_username: profile?.display_name || 'Anonymous',
            category_name: category?.name || '',
            category_color: category?.color || '#228B22',
            category_slug: category?.slug,
            slug: topic.slug,
            reply_count: topic.reply_count || 0,
            view_count: topic.view_count || 0,
            created_at: topic.created_at
          });
        });
      }

      // Search in posts
      if (filter === 'all' || filter === 'posts') {
        const { data: postResults } = await supabase
          .from('posts')
          .select(`
            id,
            content,
            created_at,
            user_id,
            topic_id,
            topics (title, slug, category_id, categories (name, color, slug))
          `)
          .ilike('content', searchTerm)
          .order('created_at', { ascending: false })
          .limit(50);

        // Get user profiles
        const userIds = [...new Set(postResults?.map(p => p.user_id).filter(Boolean))] as string[];
        let profilesMap = new Map();

        if (userIds.length > 0) {
          const { data: profiles } = await supabase
            .from('profiles')
            .select('id, display_name')
            .in('id', userIds);

          profiles?.forEach(profile => {
            profilesMap.set(profile.id, profile);
          });
        }

        postResults?.forEach(post => {
          const topic = post.topics as any;
          const category = topic?.categories;
          const profile = post.user_id ? profilesMap.get(post.user_id) : null;

          results.push({
            id: post.id,
            title: topic?.title || 'Post',
            content: post.content,
            type: 'post' as const,
            author_username: profile?.display_name || 'Anonymous',
            category_name: category?.name || '',
            category_color: category?.color || '#228B22',
            category_slug: category?.slug,
            topic_title: topic?.title,
            topic_slug: topic?.slug,
            reply_count: 0,
            view_count: 0,
            created_at: post.created_at
          });
        });
      }

      return results;
    },
    enabled: query.trim().length >= 2,
  });
};
